import { z } from 'zod';
import { COUPON_DISCOUNT_TYPES } from '../constants/coupon.js';
import { paginatedSchema, patchableSchema, uuidSchema } from './common.js';
/**
 * Percent is carried in basis points (1000 = 10%), the unit the repo already
 * uses for tax rates and restocking fees; the admin form converts once.
 * Fixed amounts are integer cents and must be positive (`moneyCentsSchema`
 * allows 0, which would be a coupon that does nothing).
 */
export const couponFieldsSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9_-]{2,64}$/, 'Use 2–64 letters, numbers, dashes or underscores'),
    discountType: z.enum(COUPON_DISCOUNT_TYPES),
    percentBps: z.number().int().min(1).max(10_000).nullable().default(null),
    amountCents: z.number().int().min(1).max(100_000_00).nullable().default(null),
    expiresAt: z.iso.datetime().nullable().default(null),
    maxUses: z.number().int().min(1).nullable().default(null),
    perCustomerLimit: z.number().int().min(1).nullable().default(null),
    isActive: z.boolean().default(true),
    note: z.string().trim().max(500).nullable().default(null),
});
export const createCouponSchema = couponFieldsSchema.refine((v) => v.discountType === 'percent'
    ? v.percentBps !== null && v.amountCents === null
    : v.amountCents !== null && v.percentBps === null, { message: 'Set a percentage for percent coupons, or an amount for dollar coupons' });
/** Only the terms that can change after a code exists. The deal itself (code,
 *  type, amount) is fixed so past redemptions are never contradicted. */
export const updateCouponSchema = patchableSchema(couponFieldsSchema.pick({
    expiresAt: true,
    maxUses: true,
    perCustomerLimit: true,
    isActive: true,
    note: true,
}));
export const couponSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    discountType: z.enum(COUPON_DISCOUNT_TYPES),
    percentBps: z.number().int().nullable(),
    amountCents: z.number().int().nullable(),
    expiresAt: z.iso.datetime().nullable(),
    maxUses: z.number().int().nullable(),
    perCustomerLimit: z.number().int().nullable(),
    isActive: z.boolean(),
    note: z.string().nullable(),
    /** Orders currently holding a redemption of this code. */
    uses: z.number().int(),
    createdAt: z.iso.datetime(),
});
export const couponListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().trim().max(200).optional(),
    isActive: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
});
export const couponListSchema = paginatedSchema(couponSchema);
//# sourceMappingURL=coupon.js.map